import type { BookingLinks } from "../../links";
import type { FlexitimeConfirmationState } from "./deriveFlexitimeConfirmationState";

// What the two flexitime email links show. Pure.

/** v1's "already confirmed" test: any confirmation value at all
 * (lodash isEmpty on the booking's `confirmation`). */
export const hasConfirmation = (confirmation: unknown) => typeof confirmation === "string" && confirmation.trim() !== "";

/**
 * A /bookings/<id>/<verb> address that is really another page. v1's legacy
 * `/<school>/bookings/<id>/review` redirect lands on `/bookings/<id>/review`,
 * which the router reads as this flow with the verb "review", and the
 * review page lives at /bookings/review/<id>; send it there.
 */
export function redirectForVerb(verb: string, bookingId: string, links: BookingLinks): string | null {
  return verb === "review" ? links.review(bookingId) : null;
}

export type ConfirmCopy = { tone: "success" | "info"; title: string; message: string; manageLink: boolean };

/**
 * The words for each state. v1's page fetched `partnerBookingID` and
 * POSTed `bookingSetConfirmation`, neither of which the API has (both
 * 404), so on v1 every one of these links ended on a blank page and
 * changed nothing; the one form it could show (an online flexitime
 * confirm) only logged its message. Nothing here pretends otherwise: an
 * already-confirmed booking says so, every other state says the link can't
 * act and where to do it instead.
 */
export function confirmCopy(state: FlexitimeConfirmationState): ConfirmCopy {
  if (state === "no-changes") {
    return { tone: "success", title: "This booking is already confirmed", message: "Nothing else needs doing.", manageLink: true };
  }
  const verb =
    state === "flexitime-about-to-reschedule"
      ? "reschedule"
      : state === "flexitime-about-to-reject" || state === "about-to-reject"
        ? "decline"
        : "confirm";
  return {
    tone: "info",
    title: "This link can’t be used any more",
    message: `To ${verb} this booking, please use the dashboard or reply to the booking email.`,
    manageLink: false,
  };
}

/** The reschedule link's date: whole unix seconds, as the email writes it. */
export function parseUnixSeconds(value: string): number | null {
  if (!/^\d{1,12}$/.test(value.trim())) return null;
  const n = Number(value);
  return n > 0 ? n : null;
}

export type RescheduleOutcome =
  | { kind: "ok" }
  | { kind: "bad-link"; message?: string }
  | { kind: "not-found" }
  | { kind: "failed" };

/** The API's answer to a reschedule confirmation, as v1's page read it:
 * 404 the booking is gone, 400 the link is wrong (hash, date, type), any
 * other failure can be retried. */
export function rescheduleOutcome(status: number | null, message?: string): RescheduleOutcome {
  if (status === null) return { kind: "ok" };
  if (status === 404) return { kind: "not-found" };
  if (status === 400) return { kind: "bad-link", message };
  return { kind: "failed" };
}
